<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <label
    :class="[$style.formField, { [$style.error]: hasError }]"
    v-bind="$attrs"
    @click="handleLabelClick"
    @keyup.enter.exact="handleLabelClick"
    @keyup.space.exact="handleLabelClick"
  >
    <span v-if="$slots.label || label" class="text-gray-70 mb-1 text-xs font-medium" :class="$style.label">
      <!-- @slot custom content of the label -->
      <slot name="label">{{ label }}</slot>
    </span>
    <!-- @slot content inside the form field -->
    <slot />

    <div class="flex w-full cursor-default gap-1 text-xs font-medium not:empty:mt-1">
      <span v-if="typeof error === 'string' && error !== ''" class="text-red-60">{{ error }}</span>
      <div v-else-if="Array.isArray(error)" class="text-red-60 flex flex-col">
        <span v-for="(message, index) in error" :key="index">
          {{ message }}
        </span>
      </div>

      <!-- @slot custom content of the help section -->
      <span v-else-if="help !== '' || $slots.help" class="text-gray-60">
        <slot name="help">{{ help }}</slot>
      </span>

      <!-- optional character count for input in default slot-->
      <span
        v-if="charCount !== undefined && maxChars"
        class="flex grow justify-end text-xs"
        :class="charsAboveMax ? 'text-red-60' : 'text-gray-60'"
      >
        {{ charCount }}/{{ maxChars }}
      </span>
    </div>
  </label>
</template>

<script setup lang="ts">
import { defineComponent, useCssModule, computed } from 'vue'

const props = withDefaults(
  defineProps<{
    label?: string // label text
    help?: string // help text
    error?: string | boolean | string[] // apply error styles or styles with error text
    charCount?: number
    maxChars?: number
    preventLabelClick?: boolean
  }>(),
  {
    label: '',
    help: '',
    error: '',
    charCount: undefined,
    maxChars: undefined,
    preventLabelClick: true,
  },
)

const style = useCssModule()

const handleLabelClick = (event: Event) => {
  if (props.preventLabelClick && event.target instanceof Element && event.target.className !== style.label) {
    event.preventDefault()
  }
}

const charsAboveMax = computed(() => Boolean(props.charCount && props.maxChars && props.charCount > props.maxChars))
const hasError = computed(() => !!props.error || (Array.isArray(props.error) && props.error.length > 0))

defineOptions({
  inheritAttrs: false,
})

defineComponent({
  name: 'UiFormField',
})
</script>

<style lang="scss" module>
.formField {
  display: block;
}

.label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.error {
  :not([data-testid='dropdown-content']) & > input,
  select {
    background-color: var(--red-10);
    border-color: var(--red-40);

    &:focus {
      border-color: var(--red-50);
      box-shadow: inset 0px 0px 4px rgba(229, 62, 62, 0.6);
    }
  }
}
</style>
